import React from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuLoaderCircle, LuSave, LuX } from 'react-icons/lu';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { JOB } from '../stageLayout';
import { ACTIVE_PROJECT_KEYS, PROJECTS, dateOfCell, jobById } from '../mockData';
import { Control, FieldLabel, Menu } from './AddVisitScene';
import CalendarScene from './CalendarScene';

const abs = (rect) => ({ left: rect.x, top: rect.y, width: rect.w, height: rect.h });

const Hint = ({ rect, children }) => (
  <span style={{ left: rect.x + 2, top: rect.y + rect.h + 6, width: rect.w }} className="tw-absolute tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
    {children}
  </span>
);

Hint.propTypes = { rect: PropTypes.object.isRequired, children: PropTypes.node };

/**
 * "Yeni İş" over the calendar, laid out like the real dialog: title, the
 * optional related project (only active assignments), dates and time.
 * `state.appear` (0–1) fades it in; values come from the timeline.
 */
const AddJobScene = ({ state }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.jobForm.${key}`, vars);
  const { appear = 0, focus, menu, menuHover, title = '', project = '', endShift = 0, saving } = state;
  const job = jobById('J3');
  const base = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const day = (col) => dateOfCell(base, job.row, col).toLocaleDateString(locale);
  const placeholder = t('profilePage.tutorial.stage.datePlaceholder');

  return (
    <>
      <CalendarScene state={state.calendar || {}} />
      {appear > 0 && (
        <>
          <div style={{ opacity: appear }} className="tw-absolute tw-inset-0 tw-bg-slate-950/45 dark:tw-bg-black/60" />
          <div
            style={{ ...abs(JOB.dialog), opacity: appear, transform: `translateY(${(1 - appear) * 24}px) scale(${0.97 + appear * 0.03})` }}
            className="tw-absolute tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white tw-shadow-2xl dark:tw-border-white/10 dark:tw-bg-[#1f232b]"
          />
          <div style={{ opacity: appear }} className="tw-absolute tw-inset-0">
            {/* Başlık */}
            <span style={{ left: 364, top: 106 }} className="tw-absolute tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-xl tw-bg-gradient-to-br tw-from-slate-400 tw-to-slate-600 tw-text-white">
              <LuBriefcase className="tw-h-[18px] tw-w-[18px]" />
            </span>
            <span style={{ left: 412, top: 102 }} className="tw-absolute tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('title')}</span>
            <span style={{ left: 412, top: 128 }} className="tw-absolute tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('subtitle')}</span>
            <span style={abs(JOB.close)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-text-slate-500"><LuX className="tw-h-[18px] tw-w-[18px]" /></span>
            <span style={{ left: JOB.dialog.x, top: 160, width: JOB.dialog.w }} className="tw-absolute tw-h-px tw-bg-slate-200 dark:tw-bg-white/10" />

            {/* İş başlığı ve ilgili proje */}
            <FieldLabel rect={JOB.title} required>{label('jobTitle')}</FieldLabel>
            <Control rect={JOB.title} value={title} placeholder={label('jobTitlePlaceholder')} focused={focus === 'title'} />
            <FieldLabel rect={JOB.project}>{label('project')}</FieldLabel>
            <Control rect={JOB.project} select value={project ? PROJECTS[project].name : ''} placeholder={label('noProject')} focused={focus === 'project'} />
            <Hint rect={JOB.project}>{label('projectHint')}</Hint>

            {/* Tarihler ve saat */}
            <FieldLabel rect={JOB.start} required>{label('startDate')}</FieldLabel>
            <Control rect={JOB.start} value={day(job.start)} placeholder={placeholder} focused={focus === 'start'} />
            <FieldLabel rect={JOB.end} required>{label('endDate')}</FieldLabel>
            <Control rect={JOB.end} value={day(job.start + endShift)} placeholder={placeholder} focused={focus === 'end'} />
            <Hint rect={JOB.end}>{label('endDateHint')}</Hint>
            <FieldLabel rect={JOB.time}>{label('time')}</FieldLabel>
            <Control rect={JOB.time} value="" placeholder="--:--" focused={focus === 'time'} />
            <Hint rect={JOB.time}>{label('timeHint')}</Hint>

            {/* Alt çubuk */}
            <span style={{ left: JOB.dialog.x, top: 556, width: JOB.dialog.w }} className="tw-absolute tw-h-px tw-bg-slate-200 dark:tw-bg-white/10" />
            <span style={abs(JOB.cancel)} className="tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-text-sm tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{t('profilePage.common.cancel')}</span>
            <span style={abs(JOB.save)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
              {saving ? <LuLoaderCircle className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave className="tw-h-4 tw-w-4" />}
              {t('profilePage.common.save')}
            </span>

            {menu === 'project' && (
              <Menu rect={JOB.project} options={[label('noProject'), ...ACTIVE_PROJECT_KEYS.map((key) => PROJECTS[key].name)]} hover={menuHover} />
            )}
          </div>
        </>
      )}
    </>
  );
};

AddJobScene.propTypes = { state: PropTypes.object.isRequired };

export default AddJobScene;
